import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ModelPicker, useModels, modelKey } from './modelInfo';
import { backdropClose } from '../backdrop';

// Shows the assembled SYSTEM + USER prompt for a pipeline node with two separate
// Copy buttons (the user pastes SYSTEM into the model's system-instruction field
// and USER into the chat). Manual mode: the model call happens outside the app in
// any model of choice; the answer comes back via the point's "Paste result" box.
const PromptModal = ({ node, pointIndex, system, user, onClose }) => {
  const [copied, setCopied] = useState('');
  const models = useModels();
  const inAppModel = modelKey(models, 'write');

  const copy = async (which, text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(which);
      setTimeout(() => setCopied(''), 1500);
    } catch (e) {
      // Clipboard API can be blocked; fall back to selecting nothing and warn.
      alert('Copy failed — select the text manually.');
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const box = {
    background: 'var(--bg, #fff)', color: 'var(--fg, #111)', width: 'min(900px, 92vw)',
    maxHeight: '88vh', overflow: 'auto', borderRadius: 8, padding: 20,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };
  const ta = {
    width: '100%', minHeight: 120, fontFamily: 'monospace', fontSize: 12,
    whiteSpace: 'pre', overflow: 'auto', marginTop: 6,
  };

  // Portal to <body>: an ancestor of the location card has a CSS transform,
  // which would otherwise turn position:fixed into "fixed inside the card".
  return createPortal(
    <div style={overlay} {...backdropClose(onClose)}>
      <div className="pl-modal" style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            Prompt: {node === '2b' ? 'Revise from comment' : 'Write point'}
            {typeof pointIndex === 'number' ? ` — point #${pointIndex + 1}` : ''}
            <ModelPicker k="write" />
          </h3>
          <button onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 13, opacity: 0.8 }}>
          Manual mode. Paste SYSTEM into your model's system-instruction field, USER into the
          chat. Then return the model's JSON into the point's "Paste result" box. (Or use
          ✨ Generate to run it in-app{inAppModel ? ` on ${inAppModel}` : ''}.)
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <strong>SYSTEM</strong>
          <button onClick={() => copy('system', system)}>
            {copied === 'system' ? '✓ Copied' : 'Copy SYSTEM'}
          </button>
        </div>
        <textarea style={ta} readOnly value={system || ''} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <strong>USER</strong>
          <button onClick={() => copy('user', user)}>
            {copied === 'user' ? '✓ Copied' : 'Copy USER'}
          </button>
        </div>
        <textarea style={ta} readOnly value={user || ''} />
      </div>
    </div>,
    document.body
  );
};

export default PromptModal;
